import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const metricCardConfigSchema = z.object({
  label: z.string().default('指标'),
  value: z.number().finite().optional(),
  unit: z.string().default(''),
  binding: z.string().default('metric'),
  field: z.string().default('value'),
  precision: z.number().int().min(0).max(8).default(2),
  tone: z.enum(['primary', 'success', 'warning', 'neutral']).default('primary'),
  trendBinding: z.string().default(''),
  trendField: z.string().default(''),
  trendMode: z.enum(['delta', 'percent']).default('delta'),
  trendPositiveDirection: z.enum(['up', 'down', 'neutral']).default('up'),
  selectionBinding: z.string().default(''),
  selectionTitle: z.string().default('当前选择'),
  selectionFields: z.array(z.string().min(1)).max(6).default([]),
  selectionLabels: z.record(z.string(), z.string()).default({}),
  trendUpColor: z.enum(['success', 'danger', 'neutral']).default('success'),
  trendDownColor: z.enum(['success', 'danger', 'neutral']).default('danger'),
}).strict();

export const metricCardDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.metric-card@1',
    category: 'information',
    title: '指标卡',
    description: '显示最新指标、变化趋势及跨视图选中实体摘要。',
    capabilities: ['scalar-display', 'dataset-binding', 'trend-display', 'selection-summary', 'compact-mode'],
    suitableFor: ['关键指标', '实时状态', '结论摘要', '监控面板'],
    inputs: [
      { name: 'metric', description: '指标数据或汇总表中的数值字段', required: false, multiple: false, acceptedDatasetKinds: ['scalar', 'series', 'table'] },
      { name: 'trend', description: '可选趋势时间序列', required: false, multiple: false, acceptedDatasetKinds: ['series'] },
      { name: 'selection', description: '可选实体摘要数据', required: false, multiple: false, acceptedDatasetKinds: ['table', 'spatial'] },
    ],
    outputs: [],
    events: [],
    constraints: ['仅展示可序列化数值'],
    configSchemaId: 'probfun.metric-card.config@1',
    configSchema: {
      type: 'object',
      properties: {
        label: { type: 'string' },
        value: { type: 'number' },
        unit: { type: 'string' },
        binding: { type: 'string' },
        field: { type: 'string' },
        precision: { type: 'integer', minimum: 0, maximum: 8 },
        tone: { type: 'string', enum: ['primary', 'success', 'warning', 'neutral'] },
        trendBinding: { type: 'string', default: '' },
        trendField: { type: 'string', default: '' },
        trendMode: { type: 'string', enum: ['delta', 'percent'], default: 'delta' },
        trendPositiveDirection: { type: 'string', enum: ['up', 'down', 'neutral'], default: 'up' },
        selectionBinding: { type: 'string', default: '' },
        selectionTitle: { type: 'string', default: '当前选择' },
        selectionFields: { type: 'array', maxItems: 6, items: { type: 'string', minLength: 1 } },
        selectionLabels: { type: 'object', additionalProperties: { type: 'string' } },
        trendUpColor: { type: 'string', enum: ['success', 'danger', 'neutral'], default: 'success' },
        trendDownColor: { type: 'string', enum: ['success', 'danger', 'neutral'], default: 'danger' },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      label: '当前值',
      unit: '',
      binding: 'metric',
      field: 'value',
      precision: 2,
      tone: 'primary',
      trendBinding: '',
      trendField: '',
      trendMode: 'delta',
      trendPositiveDirection: 'up',
      selectionBinding: '',
      selectionTitle: '当前选择',
      selectionFields: [],
      selectionLabels: {},
      trendUpColor: 'success',
      trendDownColor: 'danger',
    },
    examples: [
      { label: '平均等待时间', unit: '分钟', binding: 'metric', field: 'averageWait', precision: 2, tone: 'primary' },
      { label: '到达率', unit: '人/秒', binding: 'metric', field: 'arrivalRate', precision: 3, tone: 'success', trendBinding: 'trend', trendField: 'arrivalRate', trendMode: 'delta' },
    ],
    notes: ['若绑定数据为空，显示破折号。', '趋势使用指定输入最后两个数值；百分比模式在前值为 0 时不显示。', 'config.label 是主标题，缩小后仍显示，不作为可隐藏副标题。'],
    layout: { ...componentHardMinimum('probfun.metric-card@1'), maxHeight: 320, preferredAspectRatio: 2.5 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['summary'], preferredSize: 'small', supportsCompact: true, supportsExpanded: true, canStack: true, priority: 70 } },
  },
  configSchema: metricCardConfigSchema,
  validateBlock: (block) => {
    const config = metricCardConfigSchema.parse(block.config);
    const issues: Array<{ level: 'warning' | 'error'; path: string; message: string }> = [];
    const b = block.bindings.find(i => i.input === config.binding);
    if (b) {
      const fs = b.fields.length ? b.fields : b.field ? [b.field] : [];
      if (!fs.includes(config.field)) {
        issues.push({ level: 'error', path: 'bindings', message: `指标卡绑定缺少字段：${config.field}` });
      }
    }
    return issues;
  },
  loader: () => import('./MetricCardBlock.vue').then(m => m.default),
};

const richTextConfigSchema = z.object({
  content: z.string().max(5000).default(''),
  fontSize: z.enum(['small', 'medium', 'large']).default('medium'),
}).strict();

export const richTextDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.rich-text@1',
    category: 'information',
    title: '富文本说明',
    description: '安全渲染 Markdown 格式的实验说明、操作提示和结论总结。',
    capabilities: ['rich-text', 'markdown-render'],
    suitableFor: ['实验说明', '操作指南', '结论总结', '教学文本'],
    inputs: [],
    outputs: [],
    events: [],
    constraints: ['不执行 HTML 标签', '不渲染脚本和事件属性', '内容长度不超过 5000 字符'],
    configSchemaId: 'probfun.rich-text.config@1',
    configSchema: { type: 'object', properties: { content: { type: 'string', maxLength: 5000, default: '' }, fontSize: { type: 'string', enum: ['small', 'medium', 'large'], default: 'medium' } }, additionalProperties: false },
    defaultConfig: { content: '## 实验说明\n\n在这里编写实验的问题背景、操作步骤和结论总结。\n\n### 支持格式\n\n- **粗体** 与 *斜体*\n- 列表与编号\n- [链接](https://example.com)', fontSize: 'medium' },
    examples: [{ content: '## 实验目标\n\n观察贝叶斯公式在医学筛查中的应用。\n\n### 操作步骤\n\n1. 调节患病率滑块\n2. 观察阳性预测值变化', fontSize: 'medium' }],
    notes: ['内容仅限 Markdown 基础语法。', 'HTML 标签和事件属性会被自动剥离。'],
    layout: { ...componentHardMinimum('probfun.rich-text@1'), maxWidth: 800 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['explanation'], preferredSize: 'small', supportsCompact: true, supportsExpanded: true, canStack: true, priority: 30 } },
  },
  configSchema: richTextConfigSchema,
  loader: () => import('./RichTextBlock.vue').then(m => m.default),
};

const dataTableConfigSchema = z.object({
  binding: z.string().default('data'),
  fields: z.array(z.string().min(1)).max(50).default([]),
  columnLabels: z.record(z.string(), z.string()).default({}),
  pageSize: z.number().int().min(5).max(100).default(20),
  title: z.string().default(''),
  exampleData: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))).max(1000).default([]),
}).strict();

export const dataTableDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.data-table@1',
    category: 'information',
    title: '数据表格',
    description: '将 ObservationDataset 的行和字段渲染为可浏览的数据表。',
    capabilities: ['data-table', 'dataset-binding', 'pagination'],
    suitableFor: ['原始数据', '实验记录', '统计结果'],
    inputs: [{ name: 'data', description: '要展示的 Dataset', required: false, multiple: false, acceptedDatasetKinds: ['table', 'series', 'event-series', 'distribution'] }],
    outputs: [],
    events: [],
    constraints: ['每页最多 100 行', 'compact 模式只显示前 3 列'],
    configSchemaId: 'probfun.data-table.config@1',
    configSchema: { type: 'object', properties: { binding: { type: 'string', default: 'data' }, fields: { type: 'array', maxItems: 50, items: { type: 'string' } }, columnLabels: { type: 'object', additionalProperties: { type: 'string' }, default: {} }, pageSize: { type: 'integer', minimum: 5, maximum: 100, default: 20 }, title: { type: 'string' }, exampleData: { type: 'array', maxItems: 1000, items: { type: 'object' } } }, additionalProperties: false },
    defaultConfig: { binding: 'data', fields: [], columnLabels: {}, pageSize: 20, title: '', exampleData: [] },
    examples: [
      { binding: 'data', fields: ['id', 'value', 'time'], pageSize: 20, title: '实验数据' },
      {
        binding: 'data',
        fields: ['round', 'face', 'frequency'],
        columnLabels: { round: '轮次', face: '点数', frequency: '累计频率' },
        pageSize: 10,
        title: '掷骰子模拟结果',
        exampleData: [
          { round: 1, face: 4, frequency: 0.25 },
          { round: 2, face: 6, frequency: 0.5 },
          { round: 3, face: 2, frequency: 0.66 },
          { round: 4, face: 1, frequency: 0.5 },
          { round: 5, face: 3, frequency: 0.4 },
          { round: 6, face: 5, frequency: 0.5 },
          { round: 7, face: 4, frequency: 0.57 },
          { round: 8, face: 6, frequency: 0.62 },
        ],
      },
    ],
    notes: [
      'fields 为空时自动显示 Dataset 的所有维度字段。',
      '表头文字来自 columnLabels[字段名]，没有映射时才显示字段名本身；「轮次」等案例字样都是配置，不是写死在组件里。',
      '无绑定时回退显示 exampleData。',
    ],
    layout: { ...componentHardMinimum('probfun.data-table@1'), maxWidth: 1000 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['analysis'], preferredSize: 'medium', supportsCompact: true, supportsExpanded: true, canStack: false, priority: 35 } },
  },
  configSchema: dataTableConfigSchema,
  validateBlock: (block) => {
    // 允许无绑定，渲染空数据态
    const c = dataTableConfigSchema.safeParse(block.config);
    if (!c.success) {
      return [{
        level: 'error' as const,
        path: 'config',
        message: '数据表格配置无效',
      }];
    }
    return [];
  },
  loader: () => import('./DataTableBlock.vue').then(m => m.default),
};
